import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';

import { useOrderedGrades } from '../../hooks/useOrderedGrades';
import { useRecentSearchActions } from '../../hooks/useRecentSearchActions';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { RecentSearch } from '../../lib/recentSearches';
import {
  PILL_MIN_HEIGHT, PILL_RADIUS, pillBg, pillBgHover,
} from '../layout/headerPill';
import RecentSearchItem from './RecentSearchItem';

interface DrawerBodyProps {
  searches: RecentSearch[];
  onClose: () => void;
}

// Split out so the grades fetch (useOrderedGrades) only fires once the drawer
// is opened, not on every page where the header button is mounted.
const DrawerBody = ({ searches, onClose }: DrawerBodyProps) => {
  const { t } = useTranslation();
  const orderedGrades = useOrderedGrades();
  const {
    resume, resumeWithGrade, remove, clearAll,
  } = useRecentSearchActions();

  const handleResume = (s: RecentSearch) => {
    resume(s);
    onClose();
  };
  const handleChangeGrade = (s: RecentSearch, grade: string) => {
    resumeWithGrade(s, grade);
    onClose();
  };

  return (
    <Stack
      role="dialog"
      aria-label={t('finder.recent_searches_title')}
      sx={{ width: { xs: '100vw', sm: 340 }, height: '100%' }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{
          px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0,
        }}
      >
        <Box>
          <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>
            {t('finder.recent_searches_title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('finder.recent_searches_subtitle')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label={t('finder.close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack
        component="ul"
        spacing={1.5}
        sx={{
          listStyle: 'none',
          p: 2.5,
          m: 0,
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {searches.map((s) => (
          <RecentSearchItem
            key={s.id}
            search={s}
            orderedGrades={orderedGrades}
            onResume={handleResume}
            onChangeGrade={handleChangeGrade}
            onRemove={remove}
          />
        ))}
      </Stack>

      <Box sx={{
        px: 2.5, py: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0,
      }}
      >
        <Button
          fullWidth
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => { clearAll(); onClose(); }}
          sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'flex-start' }}
        >
          {t('finder.recent_searches_clear_all')}
        </Button>
      </Box>
    </Stack>
  );
};

interface RecentSearchesDrawerProps {
  /** When true, always show the text label (e.g. inside mobile menu overlay). */
  forceLabel?: boolean;
}

/** Header History button + right drawer of recent searches, reachable app-wide. */
const RecentSearchesDrawer = ({ forceLabel }: RecentSearchesDrawerProps = {}) => {
  const { t } = useTranslation();
  const searches = useRecentSearches();
  const [open, setOpen] = useState(false);

  if (searches.length === 0) return null;

  const close = () => setOpen(false);

  return (
    <>
      {forceLabel ? (
        // Inside mobile menu overlay: full-width list-row style
        <Button
          fullWidth
          aria-label={t('finder.recent_searches_title')}
          onClick={() => setOpen(true)}
          startIcon={<HistoryIcon sx={{ fontSize: 20, opacity: 0.9 }} />}
          sx={{
            justifyContent: 'flex-start',
            borderRadius: 0,
            px: 2,
            minHeight: 52,
            color: 'text.primary',
            fontSize: '0.875rem',
            fontWeight: 500,
            textTransform: 'none',
            '& .MuiButton-startIcon': { ml: 0, mr: 1.5 },
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {t('finder.recent_searches_title')}
        </Button>
      ) : (
        // Header pill button (compact on phones)
        <Button
          aria-label={t('finder.recent_searches_title')}
          onClick={() => setOpen(true)}
          startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: PILL_MIN_HEIGHT,
            borderRadius: PILL_RADIUS,
            px: 2,
            color: 'text.primary',
            bgcolor: pillBg,
            fontSize: '0.875rem',
            fontWeight: 500,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: pillBgHover },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('finder.recent_searches_title')}
          </Box>
        </Button>
      )}

      <Drawer anchor="right" open={open} onClose={close}>
        {open && <DrawerBody searches={searches} onClose={close} />}
      </Drawer>
    </>
  );
};

export default RecentSearchesDrawer;
